import {ref} from 'vue';
import {storeToRefs} from 'pinia';
import {useAuth} from '#imports';
import {Configuration, FreelancerNotesApi} from '@api';
import type {Profile} from '~/types/domain/Profile';
import type {ProfileNoteResource} from '../../components/admin-section-tabs/notes/types/ProfileNoteResource';

export function useProfileNotes(profile: Profile) {
    const store = useAuth();
    const {user} = storeToRefs(store);
    const profileId = profile.profileId;
    const notes = ref<ProfileNoteResource[]>([]);
    const offset = ref<Date>();
    const limit = 10;
    const loadingProfileNotes = ref<boolean>(false);

    const _apiConfiguration = new Configuration({basePath: ''});
    const _api = new FreelancerNotesApi(_apiConfiguration);

    async function fetchProfileNotes() {
        loadingProfileNotes.value = true;
        const result: ProfileNoteResource[] = await _api.getFreelancerNotes({
            profileId,
            limit,
            offset: offset.value,
        });
        if (result) {
            if (offset.value) {
                Array.prototype.push.apply(notes.value, result);
            } else {
                notes.value = result;
            }
        }
        loadingProfileNotes.value = false;
    }

    async function addProfileNote(content: string) {
        return await _api.createFreelancerNote({profileId, createFreelancerNoteRequest: {content}}).then((note) => {
            notes.value.unshift(note);
        });
    }

    async function loadMoreProfileNotes() {
        offset.value = notes.value[notes.value.length - 1].createdOn;
        await fetchProfileNotes();
    }

    async function editProfileNote(note: ProfileNoteResource, content: string) {
        await _api
            .updateFreelancerNote({profileId, freelancerNoteId: note.id, updateFreelancerNoteRequest: {content}})
            .then(() => {
                const updatedNote: ProfileNoteResource = {
                    id: note.id,
                    content,
                    createdOn: note.createdOn,
                    editedOn: new Date(),
                    author: {
                        name: user?.value?.username,
                    },
                };
                notes.value = notes.value.map((item) => (item.id === note.id ? {...item, ...updatedNote} : item));
            })
            .catch(() => {
                throw new Error(`Unable to update the note ${note.id}`);
            });
    }

    async function deleteProfileNote(freelancerNoteId: string) {
        await _api
            .deleteFreelancerNote({
                profileId,
                freelancerNoteId,
            })
            .then(() => {
                notes.value = notes.value.filter((profileNote) => profileNote.id !== freelancerNoteId);
            })
            .catch(() => {
                throw new Error(`Unable to delete the note ${freelancerNoteId}`);
            });
    }

    return {
        notes,
        fetchProfileNotes,
        addProfileNote,
        editProfileNote,
        deleteProfileNote,
        loadingProfileNotes,
        loadMoreProfileNotes,
    };
}
